import { useAction, useMutation, useQuery } from "convex/react"
import { memo, useCallback, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { useAppAuth } from "@/hooks/useAppAuth"
import { useClickOutside } from "@/hooks/useClickOutside"
import { useSolanaSign } from "@/hooks/useSolanaSign"
import { useTimeBucket } from "@/hooks/useTimeBucket"
import { useWebBalance } from "@/hooks/useWebBalance"
import { getErrorMessage } from "@/lib/errors"
import { api } from "~convex/_generated/api"
import type { Id } from "~convex/_generated/dataModel"
import { MICRO_USDC, SPOTLIGHT_QUEUE_MAX } from "~shared/constants"

/** Fallback until fees query loads. Must match SPOTLIGHT_COST_USD in convex/fees.ts. */
const SPOTLIGHT_COST_USD_FALLBACK = 100

interface PostMenuProps {
	authorHandle: string
	authorId: Id<"users">
	isSpotlight: boolean
	postId: Id<"posts">
}

export const PostMenu = memo(function PostMenu({
	authorHandle,
	authorId,
	isSpotlight,
	postId,
}: PostMenuProps) {
	const { currentUser, isAuthenticated } = useAppAuth()
	const navigate = useNavigate()
	const { signTransaction } = useSolanaSign()
	const { usdcBalance, refetchUSDC } = useWebBalance()
	const [open, setOpen] = useState(false)
	const [spotlighting, setSpotlighting] = useState(false)
	const menuRef = useRef<HTMLDivElement>(null)
	const isOwnPost = currentUser?._id === authorId
	const shouldLoadFees = open
	const shouldLoadSpotlightStatus = open && isAuthenticated && isOwnPost && !isSpotlight

	const deletePost = useMutation(api.posts.deletePost)
	const reportPost = useMutation(api.moderation.reportPost)
	const blockUser = useMutation(api.moderation.blockUser)
	const prepareSpotlight = useAction(api.swapPrepare.prepareSpotlight)
	const commitSpotlight = useAction(api.swapPrepare.commitSpotlight)

	const fees = useQuery(api.fees.getFees, shouldLoadFees ? {} : "skip")
	const spotlightCostUsd = fees?.spotlightCostUsd ?? SPOTLIGHT_COST_USD_FALLBACK

	const timeBucketMs = useTimeBucket(shouldLoadSpotlightStatus)
	const spotlightStatus = useQuery(
		api.spotlight.getSpotlightMenuStatus,
		shouldLoadSpotlightStatus ? { timeBucketMs } : "skip",
	)

	const userId = currentUser?._id ?? null

	const { spotlightBlocked, spotlightSubtitle } = useMemo(() => {
		const qLen = spotlightStatus?.queueLength ?? 0

		if (userId && spotlightStatus?.activeAuthorId === userId) {
			return { spotlightBlocked: true, spotlightSubtitle: "Your post is already live" }
		}
		if (userId && spotlightStatus?.hasCurrentUserQueued) {
			return { spotlightBlocked: true, spotlightSubtitle: "You already have a post queued" }
		}
		if (qLen >= SPOTLIGHT_QUEUE_MAX) {
			return { spotlightBlocked: true, spotlightSubtitle: "Queue is full — try again later" }
		}
		if (spotlightStatus?.hasActiveSpotlight) {
			const etaMin = Math.ceil(
				((spotlightStatus.activeExpiresAt ?? Date.now()) - Date.now()) / 60_000,
			)
			const etaText = etaMin > 0 ? `~${etaMin + qLen * 60}m` : "soon"
			return {
				spotlightBlocked: false,
				spotlightSubtitle: `Goes live in ${etaText} — $${spotlightCostUsd}`,
			}
		}
		return {
			spotlightBlocked: false,
			spotlightSubtitle: `Pin to top of feed for 1h — $${spotlightCostUsd}`,
		}
	}, [userId, spotlightCostUsd, spotlightStatus])

	const handleToggle = useCallback((e: React.MouseEvent) => {
		e.stopPropagation()
		e.preventDefault()
		setOpen(prev => !prev)
	}, [])

	const closeMenu = useCallback(() => setOpen(false), [])
	useClickOutside(menuRef, open, closeMenu)

	const handleDelete = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation()
			e.preventDefault()
			if (!window.confirm("Delete this post? This cannot be undone.")) {
				setOpen(false)
				return
			}
			deletePost({ post_id: postId })
				.then(() => toast.success("Post deleted"))
				.catch(() => toast.error("Failed to delete post"))
			setOpen(false)
		},
		[deletePost, postId],
	)

	const handleSpotlight = useCallback(
		async (e: React.MouseEvent) => {
			e.stopPropagation()
			e.preventDefault()
			setOpen(false)

			if (spotlighting || spotlightBlocked) {
				return
			}

			const costMicro = spotlightCostUsd * MICRO_USDC
			if (usdcBalance < costMicro) {
				const go = window.confirm(
					`Spotlight costs $${spotlightCostUsd}. You don't have enough USDC.\n\nGo to wallet to add funds?`,
				)
				if (go) {
					Promise.resolve(navigate("/wallet")).catch(() => {})
				}
				return
			}

			if (!window.confirm(`${spotlightSubtitle}\n\nSpotlight this post?`)) {
				return
			}

			setSpotlighting(true)
			try {
				const prepared = await prepareSpotlight({ post_id: postId })
				const signedSwapTx = await signTransaction(prepared.swapTx)
				const signedFeeTx = prepared.feeTx ? await signTransaction(prepared.feeTx) : undefined

				const result = await commitSpotlight({
					post_id: postId,
					prepare_token: prepared.prepareToken,
					signed_swap_tx: signedSwapTx,
					signed_fee_tx: signedFeeTx,
				})

				if ((result as { queued?: boolean })?.queued) {
					toast.success("Queued! You'll get a notification when your spotlight goes live")
				} else {
					toast.success("Post spotlighted for 1h!")
				}
				refetchUSDC()
			} catch (err: unknown) {
				const msg = getErrorMessage(err)
				const lower = msg.toLowerCase()
				if (lower.includes("insufficient") || lower.includes("not enough")) {
					toast.error("Insufficient balance for spotlight")
				} else {
					toast.error(msg)
				}
			} finally {
				setSpotlighting(false)
			}
		},
		[
			spotlighting,
			spotlightBlocked,
			spotlightCostUsd,
			spotlightSubtitle,
			usdcBalance,
			navigate,
			prepareSpotlight,
			commitSpotlight,
			signTransaction,
			refetchUSDC,
			postId,
		],
	)

	const handleReport = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation()
			e.preventDefault()
			reportPost({ post_id: postId })
				.then(() => toast.success("Post reported"))
				.catch(() => toast.error("Failed to report post"))
			setOpen(false)
		},
		[reportPost, postId],
	)

	const handleBlock = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation()
			e.preventDefault()
			if (!window.confirm(`Block @${authorHandle}? You won't see their posts anymore.`)) {
				setOpen(false)
				return
			}
			blockUser({ user_id: authorId })
				.then(() => toast.success(`Blocked @${authorHandle}`))
				.catch(() => toast.error("Failed to block user"))
			setOpen(false)
		},
		[blockUser, authorId, authorHandle],
	)

	return (
		<div className="relative" ref={menuRef}>
			<button
				aria-label="Post options"
				className="flex h-8 w-8 items-center justify-center rounded-full p-1 text-app-text-tertiary transition-colors hover:bg-app-surface-light hover:text-app-text-secondary"
				onClick={handleToggle}
				type="button"
			>
				<svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
					<circle cx="4" cy="10" r="1.5" />
					<circle cx="10" cy="10" r="1.5" />
					<circle cx="16" cy="10" r="1.5" />
				</svg>
			</button>

			{open && (
				<div className="absolute right-0 z-50 mt-1 min-w-[180px] animate-scale-in overflow-hidden rounded-lg border border-app-border bg-app-surface shadow-lg">
					{isOwnPost ? (
						<>
							{!isSpotlight && (
								<button
									className={`flex w-full flex-col px-4 py-2.5 text-left text-sm transition-colors hover:bg-app-surface-light ${spotlightBlocked ? "opacity-50" : "text-app-yellow"}`}
									disabled={spotlighting || spotlightBlocked}
									onClick={e => {
										handleSpotlight(e).catch(() => {})
									}}
									type="button"
								>
									<span>{spotlighting ? "Spotlighting…" : "Spotlight post"}</span>
									<span className="text-app-text-tertiary text-xs">{spotlightSubtitle}</span>
								</button>
							)}
							<button
								className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-red-500 text-sm transition-colors hover:bg-app-surface-light"
								onClick={handleDelete}
								type="button"
							>
								Delete
							</button>
						</>
					) : (
						<>
							<button
								className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-app-text text-sm transition-colors hover:bg-app-surface-light"
								onClick={handleReport}
								type="button"
							>
								Report post
							</button>
							<button
								className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-red-500 text-sm transition-colors hover:bg-app-surface-light"
								onClick={handleBlock}
								type="button"
							>
								Block @{authorHandle}
							</button>
						</>
					)}
				</div>
			)}
		</div>
	)
})
